import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { tgIcon } from "../../../images";
import PsPricesDealsPage from "../../pages/pspricesDealsPage";
import SteamGameSearch from "../../components/steamDeals/SteamGameSearch";
import { useStoreCards } from "../../components/nintendoDeals/useNintendoCards";
import {
  STEAM_STORE_ID,
  TOPUP_REGIONS,
  USD_CARDS_REGION,
  buyOptions,
  formatRub,
  useSteamPricing,
} from "../../components/steamDeals/steamTopup";
import FavoritesStrip from "../../favorites/FavoritesStrip";
import { siteImage } from "../../service/pspricesDeals.service";

const REGIONS = [
  { value: "kz", label: "Казахстан", primary: true },
  { value: "ru", label: "Россия", primary: true },
  { value: "us", label: "США" },
];

const pillClass =
  "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 font-medium shadow-sm transition";
const outlinePill = `${pillClass} border-gray-200 bg-white text-gray-700 hover:border-indigo-300 hover:text-indigo-700 dark:border-gray-600 dark:bg-[#2a2a2a] dark:text-gray-200`;
const solidPill = `${pillClass} border-indigo-600 bg-indigo-600 text-white hover:bg-indigo-500`;

function BuyLinks({ region }) {
  const topup = TOPUP_REGIONS.has(region);
  return (
    <div className="mx-auto mt-4 flex max-w-3xl flex-wrap items-center justify-center gap-2 px-1 text-sm">
      <span className="basis-full text-center text-gray-500 dark:text-gray-400 sm:mr-1 sm:basis-auto">
        Пополнение:
      </span>
      {topup ? (
        <a href="/steam" className={solidPill}>
          По логину
        </a>
      ) : null}
      <a
        href="https://t.me/trendy_steam_bot?start=ref-001"
        className={outlinePill}
        target="_blank"
        rel="noreferrer"
        onClick={() => window.ym?.(93997310, "reachGoal", "tgClickSteam")}
      >
        <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#2AABEE]">
          <img src={tgIcon} alt="" className="h-3 w-3" />
        </span>
        Telegram
      </a>
      <a href="/steam/shop" className={outlinePill}>
        Карты на сайте
      </a>
    </div>
  );
}

function toGame(deal) {
  return {
    appId: deal.nsuid,
    title: deal.name,
    cover: deal.cover,
    image: deal.coverFallback,
    url: deal.link,
    currency: deal.currency,
    price: deal.amount,
    original: deal.originalAmount,
    percent: deal.discountPercent,
  };
}

function DealBuy({ options, onBuy }) {
  const best = options[options.best];
  return (
    <div className="flex items-center justify-between gap-1 pb-1">
      <span
        className="text-xs font-medium text-indigo-700 dark:text-indigo-300"
        title="Сколько это будет стоить у нас в рублях"
      >
        {best?.approx || best?.cost ? `≈ ${formatRub(best.approx || best.cost)}` : ""}
      </span>
      <button
        type="button"
        onClick={onBuy}
        className="shrink-0 whitespace-nowrap rounded bg-indigo-600 px-1.5 py-0.5 text-[11px] font-semibold leading-tight text-white hover:bg-indigo-500"
      >
        Купить
      </button>
    </div>
  );
}

function SteamDiscount() {
  const pricing = useSteamPricing();
  const cards = useStoreCards(STEAM_STORE_ID);
  const usdCards = cards.cardsFor(USD_CARDS_REGION);
  const [dealPick, setDealPick] = useState(null);
  const [searchParams] = useSearchParams();
  const initialQuery = (searchParams.get("q") || "").slice(0, 80);

  const canBuy = (deal) => deal.amount != null && Boolean(deal.nsuid);
  const pick = (deal) => setDealPick({ game: toGame(deal), openTopup: true });
  const openFavorite = (item, deal, price) => {
    if (deal && canBuy(deal)) {
      pick(deal);
      return;
    }
    setDealPick({
      game: {
        appId: item.appId,
        title: item.title,
        cover: siteImage(item.image),
        url: item.url,
        currency: price?.currency ?? null,
        price: price?.price ?? null,
        original: price?.original ?? null,
        percent: price?.percent ?? null,
      },
      openTopup: true,
    });
  };

  return (
    <PsPricesDealsPage
      title="Скидки Steam"
      platforms="steam"
      platformQuery="steam"
      regions={REGIONS}
      ymPath="/steam-discount"
      favoritePlatform="steam"
      getDealImageAction={(deal) => (canBuy(deal) ? () => pick(deal) : null)}
      dealImageTitle="Купить эту игру"
      moreActions={
        <a
          href="https://store.steampowered.com/specials"
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center rounded-md border border-indigo-200 bg-white px-3 py-1.5 text-sm font-semibold text-indigo-700 shadow-sm hover:border-indigo-400 dark:border-indigo-700 dark:bg-[#2a2a2a] dark:text-indigo-300"
        >
          Все скидки в Steam ↗
        </a>
      }
      renderDealAction={(deal, region) =>
        canBuy(deal) ? (
          <DealBuy
            options={buyOptions(toGame(deal), region, pricing, usdCards)}
            onBuy={() => pick(deal)}
          />
        ) : null
      }
      renderTop={({ region, regions, onRegionChange, deals }) => (
        <>
          <p className="-mt-1 text-center text-sm text-gray-500 dark:text-gray-400 md:-mt-3">
            Цены Steam со скидками — и покупка под нужную игру прямо здесь
          </p>
          <SteamGameSearch
            key={initialQuery}
            region={region}
            regions={regions}
            onRegionChange={onRegionChange}
            pricing={pricing}
            usdCards={usdCards}
            cardsSettled={cards.loaded || cards.error}
            onRefreshCards={cards.refresh}
            externalPick={dealPick}
            initialQuery={initialQuery}
          />
          <BuyLinks region={region} />
          <FavoritesStrip platform="steam" region={region} deals={deals} onOpen={openFavorite} />
          <h2 className="mx-4 mt-5 text-center text-lg font-semibold dark:text-white md:mt-6">
            Популярные скидки
          </h2>
        </>
      )}
    />
  );
}

export default SteamDiscount;
